import { Box, Typography } from "@mui/material";
import { FullViewContainer, OnboardingCard, OnboardingCardV2, OnboardingImage } from "./styled";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { useTheme } from "@mui/material";
import { useInView } from "react-intersection-observer";
import { useEffect, useRef, useState } from "react";
import { RetroButton } from "components/RetroButton";
import { ReactComponent as InvitedIcon } from "assets/icons/referral-how-it-works-step-1.svg";
import { RetroCard } from "components/RetroCard";
import { Brick } from "components/Brick";
import { HeaderLogo } from "components/header/styled";
import CabalTownLogo from "assets/icons/cabal-town-logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import axiosService from "services/axios";
import { useQuery } from "@tanstack/react-query";
import { LoadingBlock } from "components/LoadingScreen";
import { onBoardingConfigs } from "v2/components/OnBoarding/utils/configs";
import useStepper from "v2/components/base/Stepper/hooks/useStepper";
import OnBoarding from "v2/components/OnBoarding/ui";

const onBoardingConfigsV2 = [
  {
    title: "SHILL is the currency of influence",
    description: "You earn SHILL when you call tokens, share insights, or trade at Cabal Town.",
    image: "/onboarding-v2-1.png",
  },
  {
    title: "The more you shill, the more it counts",
    description:
      "Push your community up the leaderboard. Stand out with every call, take, or trade.",
    image: "/onboarding-v2-2.png",
  },
  {
    title: "Trade the best alpha",
    description:
      "Cabal Town analyzes activity across each group to identify the best alpha for you.",
    image: "/onboarding-v2-3.png",
  },
];

export const OnboardingViewV2 = () => {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);

  const { skipOnboarding, handleOnboard } = useUserStoreV2();

  const { search } = useLocation();

  const theme = useTheme();

  const handleSlideChange = (index: number) => {
    setSlide(index);
  };

  const handleNextSlide = () => {
    setSlide(slide + 1);
  };

  return skipOnboarding ? null : (
    <FullViewContainer>
      <OnboardingCardV2
        sx={{
          display: "flex",
          position: "relative",
          overflowX: "scroll",
          scrollSnapType: "x mandatory",
          msScrollSnapPointsX: "repeat(100vw)",
          width: "100vw",
        }}>
        <Box
          sx={{
            position: "fixed",
            top: "24px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}>
          <Brick variant="darker">
            <Box
              width="100%"
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={theme.spacing(0.3)}>
              <HeaderLogo src={CabalTownLogo} alt="cabal-town-logo" />
            </Box>
          </Brick>
        </Box>
        {onBoardingConfigsV2.map((config, index) => {
          return (
            <OnBoardingItem
              config={config}
              index={index}
              handleSlideChange={handleSlideChange}
              slid={slide}
            />
          );
        })}
        <Box
          sx={{
            position: "fixed",
            bottom: "24px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              padding: "12px 0 16px",
            }}>
            {[0, 1, 2].map((pageIndex) => (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.border.dark}`,
                  borderRadius: "50%",
                  width: "12px",
                  height: "12px",
                  background:
                    slide === pageIndex
                      ? theme.palette.brand.primary
                      : theme.palette.surface.silver,
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0",
              gap: "10px",
              width: "100%",
              maxWidth: "400px",
            }}>
            {slide === 2 ? (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "400px",
                  padding: "0 36px",
                }}>
                <RetroButton variant="primary" onClick={handleOnboard}>
                  <Box padding="12px 0">
                    <Typography variant="title-h3">START NOW</Typography>
                  </Box>
                </RetroButton>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "400px",
                  padding: "0 36px",
                }}>
                <RetroButton variant="primary" onClick={handleNextSlide}>
                  <Box padding="12px 0">
                    <Typography variant="title-h3">Next</Typography>
                  </Box>
                </RetroButton>
              </Box>
            )}
          </Box>
        </Box>
      </OnboardingCardV2>
    </FullViewContainer>
  );
};

interface OnBoardingItemProps {
  title: string;
  description: string;
  image: string;
}

const OnBoardingItem = ({
  config,
  index,
  slid,
  handleSlideChange,
}: {
  config: OnBoardingItemProps;
  index: number;
  slid: number;
  handleSlideChange: (slide: number) => void;
}) => {
  const { title, description, image } = config;

  const { ref, inView } = useInView();
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inView) {
      handleSlideChange(index);
    }
  }, [inView]);

  useEffect(() => {
    if (slid === index && !inView) {
      scrollRef.current?.scrollIntoView();
    }
  }, [slid]);

  return (
    <Box
      ref={scrollRef}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        height: "100%",
        padding: "20px",
        minWidth: "100%",
        scrollSnapAlign: "start",
      }}>
      <Box sx={{ height: "24px" }} />
      <OnboardingImage
        ref={ref}
        src={image}
        sx={{
          width: "300px",
          aspectRatio: "1/1",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          maxWidth: "600px",
          // marginTop: "82px",
        }}>
        <Box>
          <Typography variant="title-h2" color={theme.palette.brand.primary}>
            {title}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body-md" color={theme.palette.text.secondary}>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: "96px" }} />
    </Box>
  );
};

const InvitedView = ({
  referrerId,
  onGetStarted,
}: {
  referrerId: any;
  onGetStarted: () => void;
}) => {
  const theme = useTheme();

  const { data: referrerData, isLoading: isGetReferrerLoading } = useQuery({
    queryKey: ["get-referrer", referrerId],
    queryFn: () => axiosService.getReferrerByReferrerId({ referrerId: referrerId! }),
    enabled: !!referrerId,
  });

  const handleGetStarted = () => {
    localStorage.setItem("skipInvited", "true");
    onGetStarted();
  };

  return (
    <>
      <LoadingBlock isLoading={isGetReferrerLoading}>
        <></>
      </LoadingBlock>
      <FullViewContainer
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          alignItems: "center",
          padding: "0 0 12px",
          backgroundColor: theme.palette.surface.bg,
        }}>
        <Box>
          <Brick variant="darker">
            <Box
              width="100%"
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={theme.spacing(0.3)}>
              <HeaderLogo src={CabalTownLogo} alt="cabal-town-logo" />
            </Box>
          </Brick>
        </Box>
        <Box
          sx={{
            flex: "1 auto",
            padding: "12px",
            width: "100%",
          }}>
          <RetroCard>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                height: "100%",
                gap: "24px",
                maxWidth: "520px",
                margin: "0 auto",
                padding: "0 16px",
              }}>
              <InvitedIcon />
              <Box>
                <Typography variant="title-h2">INVITATION</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 16px",
                  border: `1px solid ${theme.palette.border.dark}`,
                  background: theme.palette.surface.silver,
                  borderRadius: theme.spacing(1.5),
                  gap: "4px",
                  maxWidth: "242px",
                  width: "100%",
                }}>
                <Typography
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: "120px",
                    width: "100%",
                  }}
                  variant="subtitle">
                  {referrerData?.tgUserName}
                </Typography>
                {/* <Typography variant="subtitle">{referrerData?.tgUserName}</Typography> */}
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.border.dark}`,
                    background: theme.palette.system.green,
                    color: theme.palette.text["system-green"],
                    borderRadius: "8px",
                    padding: "0px 8px 2px",
                  }}>
                  <Typography variant="body-sm">invited you</Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body-md" color={theme.palette.text.secondary}>
                  Congratulations! You have been invited by @{referrerData?.tgUserName} to join
                  Cabal Town and trade together!
                </Typography>
              </Box>
              <Box sx={{ width: "100%" }}>
                <RetroButton variant="primary" onClick={handleGetStarted}>
                  <Box sx={{ padding: "12px 0" }}>
                    <Typography variant="title-h3">Get Started</Typography>
                  </Box>
                </RetroButton>
              </Box>
            </Box>
          </RetroCard>
        </Box>
      </FullViewContainer>
    </>
  );
};

export const OnboardingViewV3 = () => {
  const config = onBoardingConfigs.v3;
  const { slide, handleNextSlide, handleSlideChange } = useStepper();
  const { handleOnboard } = useUserStoreV2();
  const theme = useTheme();

  return (
    <OnBoarding.Wrapper
      slide={slide}
      handleNextSlide={handleNextSlide}
      onBoardingConfig={config}
      handleOnboard={handleOnboard}
      handleSlideChange={handleSlideChange}>
      {config.map((config, index) => {
        return (
          <OnBoarding.Item
            handleSlideChange={handleSlideChange}
            slid={slide}
            index={index}
            image={config.image}
            title={config.title}
            description={config.description}
          />
        );
      })}
    </OnBoarding.Wrapper>
  );
};
